import type { SecureStorage } from './secure-storage'

/**
 * A stable device identifier. It goes out as `X-Device-Id` on every request
 * and powers "active devices" and security events (backend-routes.md §1, 9/2),
 * so it must survive reloads and only change when the user clears storage.
 */
export interface DeviceApi {
  getId(): Promise<string>
  getPlatform(): 'web' | 'android' | 'ios'
  getClientVersion(): string
}

const DEVICE_ID_KEY = 'device-id'

function newId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  globalThis.crypto.getRandomValues(bytes)
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function createDeviceApi(storage: SecureStorage): DeviceApi {
  let cached: string | null = null
  let inFlight: Promise<string> | null = null

  return {
    async getId() {
      if (cached) return cached
      inFlight ??= (async () => {
        const existing = await storage.get(DEVICE_ID_KEY)
        if (existing) return existing
        const created = newId()
        await storage.set(DEVICE_ID_KEY, created)
        return created
      })()
      cached = await inFlight
      return cached
    },
    getPlatform() {
      return 'web'
    },
    getClientVersion() {
      return __APP_VERSION__
    },
  }
}
